import type { ColDef, ColGroupDef, GridApi } from 'ag-grid-community';
import { columnTypes } from '../columns';
import type { ColumnDynamics } from '../columns/types';
import { isColDef, isColGroupDef, isColumnGroup, type ColumnOrColumnGroup } from '../types';
import { safeGridApi } from '../utils';
import type { ColumnDynamicsSpec } from './ColumnDynamics';

function getColumnOrGroupColDef(column: ColumnOrColumnGroup): ColDef | ColGroupDef {
  return isColumnGroup(column)
    ? { ...column, children: column.children.map(child => columnTypes[child.type](child)) }
    : columnTypes[column.type](column);
}

export function addColumn(unsafeApi: GridApi, column: ColumnOrColumnGroup) {
  const api = safeGridApi(unsafeApi);
  if (!api) {
    return;
  }

  const currentColumnDefs = api.getColumnDefs();
  if (!currentColumnDefs) {
    return;
  }

  api.setGridOption('columnDefs', [...currentColumnDefs, getColumnOrGroupColDef(column)]);
}

export function getSaveColumnSettingsFn(
  unsafeApi: GridApi,
  dynamics: ColumnDynamics | undefined,
  spec: ColumnDynamicsSpec
) {
  return (newSettings: ColumnDynamics['state']) => {
    if (!dynamics) {
      return;
    }

    const api = safeGridApi(unsafeApi);
    if (!api) {
      return;
    }

    const columnDefs = api.getColumnDefs();
    if (!columnDefs) {
      return;
    }

    api.setGridOption(
      'columnDefs',
      columnDefs.map(colDef => {
        if (
          (isColDef(colDef) && colDef.colId === dynamics.colID) ||
          (isColGroupDef(colDef) && colDef.groupId === dynamics.colID)
        ) {
          return getColumnOrGroupColDef(spec.getColumn({ ...dynamics, state: newSettings }));
        }

        return colDef;
      })
    );
  };
}

export function removeColumn(unsafeApi: GridApi, colIDToRemove: string) {
  const api = safeGridApi(unsafeApi);
  if (!api) {
    return;
  }

  const currentColumnDefs = api.getColumnDefs();
  if (!currentColumnDefs) {
    return;
  }

  api.setGridOption(
    'columnDefs',
    currentColumnDefs.filter(colDef => {
      if (isColDef(colDef)) {
        return colDef.colId !== colIDToRemove;
      }

      return !isColumnGroupMatchForRemoval(colDef, colIDToRemove);
    })
  );
}

function isColumnGroupMatchForRemoval(colGroupDef: ColGroupDef, colIDToRemove: string): boolean {
  return (
    colGroupDef.groupId === colIDToRemove ||
    colGroupDef.children.some(child => {
      if (isColDef(child)) {
        return child.colId === colIDToRemove;
      }

      return isColumnGroupMatchForRemoval(child, colIDToRemove);
    })
  );
}
